import DOMPurify from "dompurify";
import _get from "lodash/get";
import _keyBy from "lodash/keyBy";
import _values from "lodash/values";
import _includes from "lodash/includes";
import { prepareExcerptForHTML, prepareForHTML } from "../../util/format";
import { getInitialHash } from "./navigation";

/**
 * This file contains redux selectors for accessing article related data
 */

/**
 * Return a hash map of articles for the current newsletter keyed by raven id
 * Key: internal article ID
 * Value: the article object
 * @param state
 * @returns {*}
 */
export const getArticleHashMap = (state) => {
    return _get(state, "newsletters.currentData.data.giData", {});
};

/**
 * Return whether we have tracked an article
 * @param state
 * @returns {boolean}
 */
export const getHaveTrackedSomething = (state) => {
    return _get(state, "articles.haveTrackedSomething", {});
};

/**
 * Return whether we have initially scrolled to an article
 * @param state
 * @returns {boolean}
 */
export const getHavePerformedFirstScroll = (state) => {
    return _get(state, "articles.havePerformedFirstScroll", {});
};

/**
 * Return a hash map of articles for the current newsletter keyed by grouping item id
 * Key: grouping Item ID
 * Value: the article object
 * @param state
 * @returns {*}
 */
export const getArticleGroupingIdHashMap = (state) => {
    return _keyBy(_values(getArticleHashMap(state)), "groupingItemId");
};

/**
 * Return the article data for an article in the current newsletter
 * @param state
 * @param id
 * @returns {*}
 */
export const getArticleData = (state, id) => {
    const hashMap = getArticleHashMap(state);
    return _get(hashMap, id, {});
};

/**
 * Return the article analysis id for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleAnalysisId = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "analysisId", null);
};

/**
 * Return the article group id for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleGroupId = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "articleGroupId", null);
};

/**
 * Return the article "article id" for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleArticleId = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "articleId", null);
};

/**
 * Return the article "grouping item id" for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleGroupingItemId = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "groupingItemId", null);
};

/**
 *
 * @param state
 * @param id
 */
export const getArticleGroupingItemIdMatchesInitialHash = (state, id) => {
    const groupingItemId = getArticleGroupingItemId(state, id);
    const initialHash = getInitialHash(state);
    const [hash, hashId] = initialHash.match(/#article_(\d+)/) || [];
    let numericId;
    try {
        numericId = parseInt(hashId);
    } catch (error) {
        console.log("No article id in hash. Ignoring...");
    }
    const matches = numericId == groupingItemId;
    return matches;
};

/**
 * Return the article best license for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleBestLicense = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "bestLicense", "");
};

/**
 * Return the license feed url for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleLicenseFeedUrl = (state, id) => {
    const data = getArticleData(state, id);

    return _get(data, "licenseFeedUrl", "");
};

/**
 * Return the article body for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleBody = (state, id) => {
    const data = getArticleData(state, id);
    const body = _get(data, "body", "") || "";
    return prepareForHTML(body);
};

/**
 * Return the article city state country string for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleCityStateCountryString = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "cityStateCountryString", "");
};

/**
 * Return the article comment for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleComment = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "comment", "");
};

/**
 * Return the copyright note for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getCopyrightNote = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "copyrightNote", "");
};

/**
 * Return the article date for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleDate = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "date", null);
};

/**
 * Return the article description for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleDescription = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "description", "");
};

/**
 * Return the article excerpt for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleExcerpt = (state, id) => {
    const data = getArticleData(state, id);
    const excerpt = _get(data, "excerpt", "") || "";
    return prepareExcerptForHTML(excerpt);
};

/**
 * Return the article raw excerpt (no HTML) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleExcerptRaw = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "excerptRaw", "");
};

/**
 * Return the article expired for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {boolean}
 */
export const getArticleExpired = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "expired", null);
};

/**
 * Return whether an article is a tweet for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {boolean}
 */
export const getArticleIsTweet = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "isTweet", false);
};

/**
 * Return the article formatted date for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleFormattedDate = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "formattedDate", "");
};

/**
 * Return the article formatted date for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleDateLong = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "date", "");
};

/**
 * Return the article group impressions for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleGroupImpressions = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "groupImpressions", "");
};

/**
 * Return the article id for an article in the current newsletter (hash id)
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleId = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "id", "");
};

/**
 * Return the article anchor id for an article in the current newsletter
 * This is used by ScrollableAnchor and is not actually output into the dom (also see getArticleDomId below)
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleAnchorId = (state, id) => {
    const uid = getArticleGroupingItemId(state, id);
    return `article_${uid}`;
};

/**
 * Return the article PDF id for an article in the current newsletter - also see getArticleAnchorId above
 * (To be used to locate the node to download PDF )
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleDomId = (state, id) => {
    const uid = getArticleGroupingItemId(state, id);
    return `article_pdf_${uid}`;
};

/**
 * Return the show article image value for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {boolean}
 */
export const getShowArticleImage = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "showArticleImage", false);
};

/**
 * Return the article image url for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleImageUrl = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "articleImageUrl", null);
};

/**
 * Return the article outlet name for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleOutletName = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "outletName", "");
};

/**
 * Return the article outlet URL for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleOutletUrl = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "outletUrl", "");
};

/**
 * Return the article primary author by line for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticlePrimaryAuthorByLine = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "primaryAuthorByline", "");
};

/**
 * Return whehter the article primary author is a staff writer for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {boolean}
 */
export const getArticlePrimaryAuthorIsStaffWriter = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "isPrimaryAuthorStaffWriter", false);
};

/**
 * Return the article primary impressions for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticlePrimaryImpressions = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "primaryImpressions", null);
};

/**
 * Return the article secondary author by line for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleSecondaryAuthorByLine = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "secondaryAuthorByline", "");
};

/**
 * Return the article significance for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleSignificance = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "significance", "");
};

/**
 * Return the article tracking url for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticlePrTrackingUrl = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "prTrackingUrl", "");
};

/**
 * Return the article syndication for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleSyndication = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "syndication", null);
};

/**
 * Return the article top syndicates by sharing for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleTopSyndicatesBySharing = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "topSyndicatesBySharing", null);
};

/**
 * Return the article top syndicates by impressions for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleTopSyndicatesByImpressions = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "topSyndicatesByImpressions", null);
};

/**
 * Return the article facebook shares for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleFacebookShares = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "shareInfo.facebookShares", null);
};

/**
 * Return the article pinterest shares for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticlePinterestShares = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "shareInfo.pinterestShares", null);
};

/**
 * Return the article total impressions for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleTotalImpressions = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "shareInfo.totalImpressions", null);
};

/**
 * Return the article Total Social Interactions for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleTotalShares = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "shareInfo.totalShares", null);
};

/**
 * Return the article twitter shares for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleTwitterShares = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "shareInfo.twitterShares", null);
};

/**
 * Return the article title for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleTitle = (state, id) => {
    const data = getArticleData(state, id);
    const title = _get(data, "title", "");
    return DOMPurify.sanitize(title);
};

/**
 * Return the raw article title for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleTitleRaw = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "titleRaw", "");
};

/**
 * Return the article tone for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {number}
 */
export const getArticleTone = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "tone", null);
};

/**
 * Return the article url for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleUrl = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "url", "");
};

/**
 * Return the article raw url (no redirects) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleRawUrl = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "urlRaw", "");
};

/**
 * Return the article language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleLanguage = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "articleLanguage", "");
};

/**
 * Return the article outlet language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleOutletLanguage = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "outletLanguage", "");
};

/**
 * Return the article primary author language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticlePrimaryAuthorLanguage = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "primaryAuthorLanguage", "");
};

/**
 * Return the article secondary author language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleSecondaryAuthorLanguage = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "secondaryAuthorLanguage", "");
};

/**
 * Return an array with the languages that are right to left
 * @returns {string[]}
 */
export const getRightToLeftLanguages = () => {
    return ["ar", "he"]; // arabic and hebrew
};

/**
 * Return "rtl" or "ltr" depending on the langauge passed
 * @param {string} langCode
 * @returns {string}
 */
export const getLanguageRtl = (langCode) => {
    const rtlLangs = getRightToLeftLanguages();
    return _includes(rtlLangs, langCode) ? "rtl" : "ltr";
};

/**
 * Return the left to right setting ("ltr" or "rtl") for the article language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {}
 */
export const getArticleLanguageIsRightToLeft = (state, id) => {
    const lang = getArticleLanguage(state, id);

    return getLanguageRtl(lang);
};

/**
 * Return the left to right setting ("ltr" or "rtl") for the article outlet language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleOutletLanguageIsRightToLeft = (state, id) => {
    const lang = getArticleOutletLanguage(state, id);

    return getLanguageRtl(lang);
};

/**
 * Return the left to right setting ("ltr" or "rtl") for the article primary author language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticlePrimaryAuthorLanguageIsRightToLeft = (state, id) => {
    const lang = getArticlePrimaryAuthorLanguage(state, id);

    return getLanguageRtl(lang);
};

/**
 * Return the left to right setting ("ltr" or "rtl") for the article secondary author language for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleSecondaryAuthorLanguageIsRightToLeft = (state, id) => {
    const lang = getArticleSecondaryAuthorLanguage(state, id);

    return getLanguageRtl(lang);
};

/**
 * Return the article licensing object for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleLicensing = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "licensingInfo", "");
};

/**
 * Return the article "best license text" (top licensing - Via _______) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleBestLicenseText = (state, id) => {
    const data = getArticleLicensing(state, id);
    return DOMPurify.sanitize(_get(data, "bestLicenseText", ""));
};

/**
 * Return the article "copyright note" (top licensing - Licensed Text: ________) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleCopyrightNote = (state, id) => {
    const data = getArticleLicensing(state, id);
    return DOMPurify.sanitize(_get(data, "copyrightNote", ""));
};

/**
 * Return the article "secondary copyright text" (top licensing copyright) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleSecondaryCopyrightText = (state, id) => {
    const data = getArticleLicensing(state, id);
    return DOMPurify.sanitize(_get(data, "secondaryCopyrightText", ""));
};

/**
 * Return the article "copyright text" (bottom licensing copyright) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleCopyrightText = (state, id) => {
    const data = getArticleLicensing(state, id);
    return DOMPurify.sanitize(_get(data, "copyrightText", ""));
};

/**
 * Return the article "copy1Url" (bottom licensing copyright) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleCopy1Url = (state, id) => {
    const data = getArticleLicensing(state, id);
    return _get(data, "copy1Url", "");
};

/**
 * Return the article "copyright text" (bottom licensing copyright) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {string}
 */
export const getArticleCopy1UrlAnchorText = (state, id) => {
    const data = getArticleLicensing(state, id);
    return _get(data, "copy1UrlAnchorText", "");
};

/**
 * Return the article "terms Of Use Href" (bottom licensing - URL to be used for terms of use) for an article in the current newsletter
 * @param {*} state
 * @param {number} id
 * @returns {*}
 */
export const getArticleTermsOfUseHref = (state, id) => {
    const data = getArticleLicensing(state, id);
    return _get(data, "termsOfUseHref", "");
};

/**
 * gets the list if "associated articles" for the given article
 * these articles are displayed differently than the "top level" articles they are associated with
 * @param {*} state
 * @param {number} id
 * @returns {Array} - list of article gi ids (not article objects);
 *                    empty array if there are no assoicated articles
 */
export const getArticleAssociatedArticles = (state, id) => {
    const data = getArticleData(state, id);
    return _get(data, "associatedGiList", []);
};
